// MigrationModal.js - Popup announcing the migration to Voxia v2
// Shown once per day on all platforms (web, Electron, mobile)
import React, { useState, useEffect } from 'react';
import Button from '../buttons/Button';
import { openExternalUrl } from '../../utils/browserHelper';
import './MigrationModal.css';

const STORAGE_KEY = 'v2_migration_popup_last_shown';
const NEW_APP_URL = 'https://app.voxia-md.ch/login';

// Local date as YYYY-MM-DD so the popup reappears once per calendar day
const getTodayKey = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

export const shouldShowMigrationModal = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== getTodayKey();
  } catch (e) {
    return false;
  }
};

const markShownToday = () => {
  try {
    localStorage.setItem(STORAGE_KEY, getTodayKey());
  } catch (e) {
    // ignore storage errors
  }
};

const MigrationModal = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (shouldShowMigrationModal()) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    markShownToday();
    setVisible(false);
  };

  const handleGoToNewVoxia = () => {
    dismiss();
    openExternalUrl(NEW_APP_URL);
  };

  // Clickable link to the new site (opens via openExternalUrl so it works on
  // web, Electron and Capacitor alike)
  const NewVoxiaLink = () => (
    <a
      href={NEW_APP_URL}
      className="migration-modal-link"
      onClick={(e) => {
        e.preventDefault();
        openExternalUrl(NEW_APP_URL);
      }}
    >
      app.voxia-md.ch
    </a>
  );

  return (
    <div className="migration-modal-overlay">
      <div className="migration-modal">
        <div className="migration-modal-header">
          <h2>Voxia fait peau neuve 🎉</h2>
        </div>

        <div className="migration-modal-body">
          <p>
            Une nouvelle version de Voxia est disponible <strong>sur un nouveau site : </strong><NewVoxiaLink />.
          </p>

          <div className="migration-modal-todo">

            <p className="migration-modal-todo-main">
             Mettez votre application mobile à jour dès maintenant, puis accédez à <NewVoxiaLink />.
            </p>
          </div>

          <p>
<strong>Vos modèles, rapports et templates ont été migrés sur le nouveau site.</strong>{' '}
            A partir de mainteant, vos rapports restent dans la version où vous les créez.
            Un rapport créé dans l'ancien voxia (voxia-md.ch) n'apparaît pas dans le nouveau
            Voxia (app.voxia-md.ch), et inversement. Pour retrouver un rapport, ouvrez la version
            dans laquelle vous l'avez créé.
          </p>

          <p>
            L'ancien site restera accessible jusqu'au <strong>31 octobre 2026</strong>, puis
            sera archivé.
          </p>

          <p>
            Au programme : nouveaux modèles d'IA plus performants, synchronisation multi-appareils de la dictée, sections personnalisables et
            l'assistant Voxia. Vos données restent toujours hébergées en Suisse, à Genève.
          </p>
        </div>

        <div className="migration-modal-actions">
          <Button
            variant="primary"
            onClick={handleGoToNewVoxia}
          >
            Accéder au nouveau Voxia →
          </Button>
          <Button
            variant="secondary"
            onClick={dismiss}
          >
            Plus tard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MigrationModal;
